import cx from "classnames";
import styles from "./StatusIcon.module.scss";
import { Icon, IconProps } from "../../../components/Icon";
import type { PassThroughPropsWithRef } from "../../../types";

import error_dark from "./svg/error_dark.svg";
import warning_dark from "./svg/warning_dark.svg";
import success_dark from "./svg/success_dark.svg";
import error_light from "./svg/error_light.svg";
import warning_light from "./svg/warning_light.svg";
import success_light from "./svg/success_light.svg";
import { useTheme } from "../../../providers";

const iconMap = {
  success: {
    light: success_light,
    dark: success_dark,
  },
  warning: {
    light: warning_light,
    dark: warning_dark,
  },
  error: {
    light: error_light,
    dark: error_dark,
  },
};

/**
 * The visual variant of the StatusIcon.
 */
export type StatusIconVariant = "success" | "warning" | "error";

/**
 * Props for the StatusIcon component
 * @property {StatusIconVariant} variant - The visual variant of the status icon
 */
export type StatusIconProps = {
  /**
   * The visual variant of the status icon.
   */
  variant: StatusIconVariant;
  /**
   * Props spread onto the root `<span>` element.
   */
  rootProps?: PassThroughPropsWithRef<"span">;
  /**
   * Additional class names to apply to the root `<span>` element.
   */
  className?: string;
  /**
   * Props spread onto the inner `<Icon>` element.
   */
  iconProps?: Omit<IconProps, "svg" | "inherit" | "color">;
};

/**
 * StatusIcon component for displaying a status indicator icon.
 *
 * Features:
 * - Supports success, warning, and error variants
 * - Uses semantic status colors from design tokens
 * - Lightweight and composable
 *
 * @example
 * <StatusIcon variant="success" />
 * <StatusIcon variant="warning" />
 * <StatusIcon variant="error" />
 */
export const StatusIcon = (props: StatusIconProps) => {
  const { variant, rootProps, className, iconProps } = props;

  const { mode } = useTheme();

  return (
    <span
      {...rootProps}
      className={cx(
        styles["status-icon"],
        styles[variant],
        className,
        rootProps?.className,
      )}
    >
      <Icon inherit svg={iconMap[variant][mode ?? "light"]} {...iconProps} />
    </span>
  );
};

StatusIcon.displayName = "StatusIcon";
